import React, { useEffect, useState } from 'react';
import { Drawer, Box, Typography, TextField, Button, IconButton, FormControl, InputLabel, Select, MenuItem, FormHelperText, useTheme, useMediaQuery, InputBase, InputAdornment, Divider, Tooltip } from '@mui/material';
import { XIcon, SearchIcon, InfoIcon } from 'lucide-react';
interface SubAgentUserFormProps {
  open: boolean;
  user: any | null;
  onClose: () => void;
  onSave: (userData: any) => void;
  preselectedSubAgent: string | null;
}
interface FormData {
  name: string;
  fatherName: string;
  passport: string;
  email: string;
  phone: string;
  username: string;
  branchCode: string;
  settlementAccount: string;
  subAgent: string;
  userType: 'maker' | 'checker';
  country: string;
}
interface FormErrors {
  name?: string;
  fatherName?: string;
  passport?: string;
  email?: string;
  phone?: string;
  username?: string;
  branchCode?: string;
  settlementAccount?: string;
  subAgent?: string;
  userType?: string;
  country?: string;
}
const SUB_AGENTS = ['Paga Ghana', 'MoneyGram Nigeria', 'WorldRemit Kenya', 'Flutterwave Ghana', 'Flutterwave Nigeria', 'M-Pesa Kenya', 'Orange Money Senegal', 'Accra Money Center', 'Lagos Express', 'Nairobi Remit Center'];
const BRANCH_CODES = ['GH-ACC-001', 'GH-KUM-001', 'NG-LAG-001', 'NG-ABJ-001', 'SN-DKR-001', 'UG-KLA-001', 'KE-NBI-001', 'CI-ABJ-001'];
const COUNTRIES = ['Ghana', 'Nigeria', 'Senegal', 'Uganda', 'Kenya', "Côte d'Ivoire", 'Togo', 'Cameroon', 'Benin'];
const SubAgentUserFormDrawer: React.FC<SubAgentUserFormProps> = ({
  open,
  user,
  onClose,
  onSave,
  preselectedSubAgent
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    fatherName: '',
    passport: '',
    email: '',
    phone: '',
    username: '',
    branchCode: '',
    settlementAccount: '',
    subAgent: '',
    userType: 'maker',
    country: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [subAgentSearchText, setSubAgentSearchText] = useState('');
  const [branchCodeSearchText, setBranchCodeSearchText] = useState('');
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        fatherName: user.fatherName || '',
        passport: user.cnic || user.passport || '',
        email: user.email || '',
        phone: user.phone || '',
        username: user.username || '',
        branchCode: user.branchCode || '',
        settlementAccount: user.settlementAccount || '',
        subAgent: user.subAgent || '',
        userType: user.userType || 'maker',
        country: user.country || ''
      });
    } else {
      setFormData({
        name: '',
        fatherName: '',
        passport: '',
        email: '',
        phone: '',
        username: '',
        branchCode: '',
        settlementAccount: '',
        subAgent: preselectedSubAgent || '',
        userType: 'maker',
        country: ''
      });
    }
    setErrors({});
  }, [user, open, preselectedSubAgent]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | {
    name?: string;
    value: unknown;
  }>) => {
    const {
      name,
      value
    } = e.target;
    if (name) {
      setFormData({
        ...formData,
        [name]: value
      });
      // Clear error when field is edited
      if (errors[name as keyof FormErrors]) {
        setErrors({
          ...errors,
          [name]: undefined
        });
      }
    }
  };
  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (formData.passport.trim() && !/^[A-Z0-9]{6,12}$/i.test(formData.passport)) {
      newErrors.passport = 'Passport/ID should be alphanumeric, 6-12 characters';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\+\d{7,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'International format required, e.g. +233 20 123 4567';
    }
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (!/^[a-z0-9.]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain lowercase letters, numbers, and dots';
    }
    if (!formData.branchCode) {
      newErrors.branchCode = 'Branch code is required';
    }
    if (!formData.settlementAccount.trim()) {
      newErrors.settlementAccount = 'Settlement account is required';
    }
    if (!formData.subAgent) {
      newErrors.subAgent = 'Sub Agent is required';
    }
    if (!formData.userType) {
      newErrors.userType = 'User Type is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = () => {
    if (validate()) {
      onSave({
        ...formData,
        id: user?.id
      });
    }
  };
  return <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{
    sx: {
      width: {
        xs: '100%',
        sm: 400
      },
      p: 0
    }
  }} data-id="element-2035">
      <Box sx={{
      p: 3
    }} data-id="element-2036">
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3
      }} data-id="element-2037">
          <Typography variant="h6" data-id="element-2038">
            {user ? 'Edit User' : 'Add New User'}
          </Typography>
          <IconButton onClick={onClose} size="small" data-id="element-2039">
            <XIcon size={18} data-id="element-2040" />
          </IconButton>
        </Box>
        <Box component="form" noValidate autoComplete="off" sx={{
        mt: 2
      }} data-id="element-2041">
          <TextField fullWidth margin="normal" label="Name" name="name" value={formData.name} onChange={handleChange} error={!!errors.name} helperText={errors.name} required data-id="element-2042" />
          <TextField fullWidth margin="normal" label="Department" name="fatherName" value={formData.fatherName} onChange={handleChange} error={!!errors.fatherName} helperText={errors.fatherName} data-id="element-2043" />
          <TextField fullWidth margin="normal" label="Passport/ID (Optional)" name="passport" value={formData.passport} onChange={handleChange} error={!!errors.passport} helperText={errors.passport || 'Alphanumeric, e.g. GH1234567'} placeholder="GH1234567" data-id="element-2044" />
          <TextField fullWidth margin="normal" label="Email" name="email" type="email" value={formData.email} onChange={handleChange} error={!!errors.email} helperText={errors.email} required data-id="element-2045" />
          <TextField fullWidth margin="normal" label="Phone" name="phone" value={formData.phone} onChange={handleChange} error={!!errors.phone} helperText={errors.phone || 'International format, e.g. +233 20 123 4567'} required data-id="element-2046" />
          <TextField fullWidth margin="normal" label="Username" name="username" value={formData.username} onChange={handleChange} error={!!errors.username} helperText={errors.username || 'Lowercase letters, numbers, and dots only'} required data-id="element-2047" />
          <FormControl fullWidth margin="normal" error={!!errors.branchCode} required data-id="element-2048">
            <InputLabel id="branch-code-label" data-id="element-2049">Branch Code</InputLabel>
            <Select labelId="branch-code-label" name="branchCode" value={formData.branchCode} onChange={handleChange} label="Branch Code" MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300
              },
              sx: {
                '& .MuiMenuItem-root': {
                  fontSize: '0.875rem'
                }
              }
            }
          }} data-id="element-2050">
              <MenuItem data-id="element-2051">
                <InputBase placeholder="Search branch codes..." startAdornment={<InputAdornment position="start" data-id="element-2053">
                      <SearchIcon size={16} data-id="element-2054" />
                    </InputAdornment>} onClick={e => e.stopPropagation()} onChange={e => {
                setBranchCodeSearchText(e.target.value.toLowerCase());
              }} sx={{
                p: 1,
                width: '100%',
                fontSize: '0.875rem',
                '& .MuiInputBase-input': {
                  p: 0
                }
              }} data-id="element-2052" />
              </MenuItem>
              <Divider data-id="element-2055" />
              {BRANCH_CODES.filter(code => branchCodeSearchText === '' || code.toLowerCase().includes(branchCodeSearchText)).map(code => <MenuItem key={code} value={code} data-id="element-2056">
                  {code}
                </MenuItem>)}
            </Select>
            {errors.branchCode && <FormHelperText data-id="element-2057">{errors.branchCode}</FormHelperText>}
          </FormControl>
          <TextField fullWidth margin="normal" label="Settlement Account (Branch/User)" name="settlementAccount" value={formData.settlementAccount} onChange={handleChange} error={!!errors.settlementAccount} helperText={errors.settlementAccount} required data-id="element-2058" />
          <FormControl fullWidth margin="normal" data-id="element-2059">
            <InputLabel id="country-label" data-id="element-2060">Country (Optional)</InputLabel>
            <Select labelId="country-label" name="country" value={formData.country} onChange={handleChange} label="Country (Optional)" data-id="element-2061">
              <MenuItem value="" data-id="element-2062">
                <em data-id="element-2063">None</em>
              </MenuItem>
              {COUNTRIES.map(country => <MenuItem key={country} value={country} data-id="element-2064">
                  {country}
                </MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal" error={!!errors.subAgent} required data-id="element-2065">
            <InputLabel id="sub-agent-label" data-id="element-2066">Sub Agent</InputLabel>
            <Select labelId="sub-agent-label" name="subAgent" value={formData.subAgent} onChange={handleChange} label="Sub Agent" MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300
              },
              sx: {
                '& .MuiMenuItem-root': {
                  fontSize: '0.875rem'
                }
              }
            }
          }} data-id="element-2067">
              <MenuItem data-id="element-2068">
                <InputBase placeholder="Search sub agents..." startAdornment={<InputAdornment position="start" data-id="element-2070">
                      <SearchIcon size={16} data-id="element-2071" />
                    </InputAdornment>} onClick={e => e.stopPropagation()} onChange={e => {
                setSubAgentSearchText(e.target.value.toLowerCase());
              }} sx={{
                p: 1,
                width: '100%',
                fontSize: '0.875rem',
                '& .MuiInputBase-input': {
                  p: 0
                }
              }} data-id="element-2069" />
              </MenuItem>
              <Divider data-id="element-2072" />
              {SUB_AGENTS.filter(agent => subAgentSearchText === '' || agent.toLowerCase().includes(subAgentSearchText)).map(subAgent => <MenuItem key={subAgent} value={subAgent} data-id="element-2073">
                  {subAgent}
                </MenuItem>)}
            </Select>
            {errors.subAgent && <FormHelperText data-id="element-2074">{errors.subAgent}</FormHelperText>}
          </FormControl>
          <FormControl fullWidth margin="normal" error={!!errors.userType} required data-id="element-2075">
            <InputLabel id="user-type-label" data-id="element-2076">User Type</InputLabel>
            <Select labelId="user-type-label" name="userType" value={formData.userType} onChange={handleChange} label="User Type" endAdornment={<Tooltip title="Select user's operational role (Maker creates, Checker reviews/approves)." placement="top" arrow data-id="element-2078">
                  <IconButton size="small" sx={{
              mr: 0.5,
              color: 'rgba(0, 0, 0, 0.54)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }} onClick={e => e.stopPropagation()} data-id="element-2079">
                    <InfoIcon size={16} data-id="element-2080" />
                  </IconButton>
                </Tooltip>} data-id="element-2077">
              <MenuItem value="maker" data-id="element-2081">Maker</MenuItem>
              <MenuItem value="checker" data-id="element-2082">Checker</MenuItem>
            </Select>
            <FormHelperText data-id="element-2083">
              {errors.userType || "Select user's operational role (Maker creates, Checker reviews/approves)."}
            </FormHelperText>
          </FormControl>
        </Box>
      </Box>
      <Box sx={{
      position: 'sticky',
      bottom: 0,
      backgroundColor: 'background.paper',
      p: 3,
      borderTop: '1px solid #E5E7EB'
    }} data-id="element-2084">
        <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2
      }} data-id="element-2085">
          <Button variant="outlined" onClick={onClose} data-id="element-2086">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} data-id="element-2087">
            Save
          </Button>
        </Box>
      </Box>
    </Drawer>;
};
export default SubAgentUserFormDrawer;